import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Button, LoadingSpinner } from '../components/Layout';
import { format, parseISO, startOfWeek } from 'date-fns';
import api from '../services/api';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

// Safe date formatting helper to prevent crashes
const safeFormatDate = (dateStr: string | undefined | null, formatStr: string, fallback = 'N/A'): string => {
  if (!dateStr) return fallback;
  try {
    const date = parseISO(dateStr);
    if (isNaN(date.getTime())) return fallback;
    return format(date, formatStr);
  } catch {
    return fallback;
  }
};

interface ShiftRecord {
  id: string;
  date: string;
  clockIn: string;
  clockOut?: string;
  totalHours?: number;
  workstationName?: string;
}

interface ScheduleShift {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  workstationName: string;
  type: 'regular' | 'overtime' | 'training';
}

interface EmployeeInfo {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  department: string;
  hireDate?: string;
  workstationTypes: string[];
  permissions: {
    workstation_access: boolean;
    workstation_types: string[];
  };
}

export function ProfilePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [employeeInfo, setEmployeeInfo] = useState<EmployeeInfo | null>(null);
  const [clockHistory, setClockHistory] = useState<ShiftRecord[]>([]);
  const [upcomingShifts, setUpcomingShifts] = useState<ScheduleShift[]>([]);
  const [weeklyHours, setWeeklyHours] = useState<{ total: number; target: number }>({ total: 0, target: 40 });
  // Self-service state
  const [contact, setContact] = useState({ workPhone: '', mobilePhone: '', emergencyContactName: '', emergencyContactPhone: '', street: '', city: '', state: '', zip: '' });
  const [contactMsg, setContactMsg] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  const [isEmployeeRecord, setIsEmployeeRecord] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    loadProfileData();
  }, []);

  const saveContact = async () => {
    setSavingContact(true); setContactMsg('');
    try {
      const res = await fetch(`${API_URL}/employees/me`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(contact) });
      if (!res.ok) throw new Error('save failed');
      setContactMsg('✓ Saved');
      setTimeout(() => setContactMsg(''), 3000);
    } catch { setContactMsg('Could not save — try again'); }
    finally { setSavingContact(false); }
  };

  const changePassword = async () => {
    setPwMsg(''); setPwErr(false);
    if (pw.next.length < 8) { setPwErr(true); setPwMsg('New password must be at least 8 characters'); return; }
    if (pw.next !== pw.confirm) { setPwErr(true); setPwMsg('New passwords do not match'); return; }
    setSavingPw(true);
    try {
      const res = await fetch(`${API_URL}/auth/change-password`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ currentPassword: pw.current, newPassword: pw.next }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Current password is incorrect');
      setPwErr(false); setPwMsg('✓ Password changed'); setPw({ current: '', next: '', confirm: '' });
      setTimeout(() => setPwMsg(''), 4000);
    } catch (e: any) { setPwErr(true); setPwMsg(e?.message || 'Failed — check your current password'); }
    finally { setSavingPw(false); }
  };

  const loadProfileData = async () => {
    try {
      // Load employee info from user context and API
      const info: EmployeeInfo = {
        id: (user as any)?.employeeId || user?.id || '',
        firstName: user?.name?.split(' ')[0] || '',
        lastName: user?.name?.split(' ').slice(1).join(' ') || '',
        email: user?.email || '',
        jobTitle: (user as any)?.jobTitle || 'Production Team Member',
        department: (user as any)?.department || 'Production',
        hireDate: (user as any)?.hireDate,
        workstationTypes: (user as any)?.permissions?.workstation_types || [],
        permissions: (user as any)?.permissions || { workstation_access: true, workstation_types: [] },
      };
      // Resolve workstation types to human-readable names (include inactive for name resolution)
      if (info.workstationTypes.length > 0) {
        try {
          const allWs = await api.getAllWorkstations();
          if (Array.isArray(allWs)) {
            info.workstationTypes = info.workstationTypes
              .map(wsType => {
                const match = allWs.find((ws: any) =>
                  ws.id === wsType || ws.code === wsType ||
                  ws.code?.toLowerCase() === wsType.toLowerCase() ||
                  ws.name?.toLowerCase() === wsType.toLowerCase()
                );
                if (match) {
                  return match.active !== false ? match.name : null; // Hide inactive stations
                }
                return wsType.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
              })
              .filter((name): name is string => name !== null);
          }
        } catch (e) {
          console.log('Could not resolve workstation names');
        }
      }

      setEmployeeInfo(info);

      // Pull the full employee record for the editable contact fields.
      try {
        const me = (await api.get('/employees/me')).data;
        if (me?.id) {
          setIsEmployeeRecord(true);
          setContact({
            workPhone: me.workPhone || me.work_phone || '',
            mobilePhone: me.mobilePhone || me.mobile_phone || '',
            emergencyContactName: me.emergencyContactName || me.emergency_contact_name || '',
            emergencyContactPhone: me.emergencyContactPhone || me.emergency_contact_phone || '',
            street: me.street || '', city: me.city || '', state: me.state || '', zip: me.zip || '',
          });
        }
      } catch { /* not an employee-table record (e.g. legacy users) — hide contact editor */ }

      // Pay period = Thursday→Wednesday
      const _now = new Date();
      const _dow = _now.getDay();
      const _daysBack = (_dow - 4 + 7) % 7;
      const _thuStart = new Date(_now);
      _thuStart.setDate(_now.getDate() - _daysBack);
      const weekStart = format(_thuStart, 'yyyy-MM-dd');
      try {
        const hours = await api.getWeeklyHours(weekStart);
        setWeeklyHours({ total: hours?.totalHours || 0, target: 40 });
      } catch (e) {
        console.log('Could not fetch weekly hours');
      }

      // Load shift history
      try {
        const shifts = await api.getMyShifts();
        const history: ShiftRecord[] = (shifts || []).slice(0, 10).map((s: any) => ({
          id: s.id,
          date: s.date || s.shift_date,
          clockIn: s.clockIn || s.clock_in,
          clockOut: s.clockOut || s.clock_out,
          totalHours: s.totalHours || s.hours_worked,
          workstationName: s.workstationName || s.workstation_name,
        }));
        setClockHistory(history);
      } catch (e) {
        console.log('Could not fetch clock history');
        setClockHistory([]);
      }

      // Load upcoming shifts
      try {
        const upcoming = await api.getMyShifts(new Date().getMonth() + 1, new Date().getFullYear());
        const futureShifts: ScheduleShift[] = (upcoming || [])
          .filter((s: any) => new Date(s.date || s.shift_date) >= new Date())
          .slice(0, 5)
          .map((s: any) => ({
            id: s.id,
            date: s.date || s.shift_date,
            startTime: s.startTime || s.start_time || '07:00',
            endTime: s.endTime || s.end_time || '15:30',
            workstationName: s.workstationName || s.workstation_name || 'TBD',
            type: s.type || 'regular',
          }));
        setUpcomingShifts(futureShifts);
      } catch (e) {
        console.log('Could not fetch upcoming shifts');
        setUpcomingShifts([]);
      }
    } catch (error) {
      console.error('Failed to load profile data:', error);
    } finally {
      setLoading(false);
    }
  };


  const formatShiftType = (type: string) => {
    const styles: Record<string, string> = {
      regular: 'bg-blue-500/20 text-blue-400',
      overtime: 'bg-orange-500/20 text-orange-400',
      training: 'bg-purple-500/20 text-purple-400',
    };
    return styles[type] || styles.regular;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white">My Profile</h1>

      {/* Employee Info Card */}
      <Card>
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full bg-yellow-400 flex items-center justify-center text-2xl font-bold text-gray-900">
            {employeeInfo?.firstName?.charAt(0) || user?.name?.charAt(0) || 'U'}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-white">
              {employeeInfo?.firstName} {employeeInfo?.lastName}
            </h2>
            <p className="text-gray-400">{employeeInfo?.jobTitle}</p>
            <p className="text-gray-500 text-sm">{employeeInfo?.department}</p>
            <p className="text-gray-500 text-sm mt-1">{employeeInfo?.email}</p>
          </div>
        </div>

        {/* Employee ID Section */}
        <div className="mt-4 pt-4 border-t border-gray-700">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase">Employee ID</p>
              <p className="text-white font-mono text-lg">{employeeInfo?.id || user?.employeeId || user?.id || 'N/A'}</p>
            </div>
            {employeeInfo?.hireDate && (
              <div>
                <p className="text-xs text-gray-500 uppercase">Hire Date</p>
                <p className="text-white">{safeFormatDate(employeeInfo.hireDate, 'MMM d, yyyy')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Factory Admin Link (pay / permissions are still HR-managed) */}
        <div className="mt-4 pt-4 border-t border-gray-700">
          <p className="text-xs text-gray-500 mb-2">Pay rate, job title & permissions are managed by HR.</p>
          <a
            href="https://factory.kederscreens.com/hr/employees"
            target="_blank"
            rel="noopener noreferrer"
            className="text-yellow-400 hover:text-yellow-300 text-sm flex items-center gap-1"
          >
            Contact HR via Factory Admin
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </Card>

      {/* Contact Information — self-service (only for employee records) */}
      {isEmployeeRecord && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-yellow-400">Contact Information</h3>
            {contactMsg && <span className={`text-sm ${contactMsg.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>{contactMsg}</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {([
              ['workPhone', 'Work Phone'], ['mobilePhone', 'Mobile Phone'],
              ['emergencyContactName', 'Emergency Contact Name'], ['emergencyContactPhone', 'Emergency Contact Phone'],
              ['street', 'Street Address'], ['city', 'City'], ['state', 'State'], ['zip', 'ZIP'],
            ] as [keyof typeof contact, string][]).map(([key, label]) => (
              <div key={key}>
                <label className="block text-xs text-gray-400 mb-1">{label}</label>
                <input
                  value={contact[key]}
                  onChange={(e) => setContact({ ...contact, [key]: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-[#111] border border-[#333] text-white focus:border-[#FFCD24] focus:outline-none"
                />
              </div>
            ))}
          </div>
          <div className="mt-4">
            <Button onClick={saveContact} disabled={savingContact}>
              {savingContact ? 'Saving…' : 'Save Contact Info'}
            </Button>
          </div>
        </Card>
      )}

      {/* Change Password — self-service */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-yellow-400">Change Password</h3>
          {pwMsg && <span className={`text-sm ${pwErr ? 'text-red-400' : 'text-green-400'}`}>{pwMsg}</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Current Password</label>
            <input type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-[#111] border border-[#333] text-white focus:border-[#FFCD24] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">New Password</label>
            <input type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-[#111] border border-[#333] text-white focus:border-[#FFCD24] focus:outline-none" placeholder="Min 8 characters" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Confirm New Password</label>
            <input type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-[#111] border border-[#333] text-white focus:border-[#FFCD24] focus:outline-none" />
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={changePassword} disabled={savingPw || !pw.current || !pw.next}>
            {savingPw ? 'Updating…' : 'Change Password'}
          </Button>
        </div>
      </Card>

      {/* Time Clock Card */}
      <Card className="border-2 border-yellow-400/50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-yellow-400">Time Clock</h3>
            <p className="text-gray-400 text-sm">
              {Number(weeklyHours.total || 0).toFixed(1)} / {weeklyHours.target} hrs this week
            </p>
          </div>
          <Button variant="secondary" onClick={() => navigate('/time-clock')}>
            Open Time Clock
          </Button>
        </div>
      </Card>

      {/* Assigned Workstations */}
      <Card>
        <h3 className="text-lg font-semibold text-yellow-400 mb-4">Assigned Workstations</h3>
        {employeeInfo?.workstationTypes && employeeInfo.workstationTypes.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {employeeInfo.workstationTypes.map((ws, idx) => (
              <span
                key={idx}
                className="px-3 py-2 bg-blue-500/20 text-blue-400 rounded-lg text-sm font-medium"
              >
                {ws.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <span className="px-3 py-2 bg-green-500/20 text-green-400 rounded-lg text-sm font-medium">
              All Workstations
            </span>
            <p className="w-full text-gray-500 text-sm mt-2">
              You have access to all workstation types
            </p>
          </div>
        )}
      </Card>

      {/* Upcoming Schedule */}
      <Card>
        <h3 className="text-lg font-semibold text-yellow-400 mb-4">Upcoming Schedule</h3>
        {upcomingShifts.length > 0 ? (
          <div className="space-y-3">
            {upcomingShifts.map((shift) => (
              <div
                key={shift.id}
                className="flex items-center justify-between bg-gray-800 rounded-lg p-3"
              >
                <div>
                  <div className="text-white font-medium">
                    {safeFormatDate(shift.date, 'EEEE, MMM d')}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {shift.startTime} - {shift.endTime}
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${formatShiftType(shift.type)}`}>
                    {shift.type.toUpperCase()}
                  </span>
                  <div className="text-gray-400 text-sm mt-1">{shift.workstationName}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No upcoming shifts scheduled</p>
        )}
      </Card>

      {/* Clock In/Out History */}
      <Card>
        <h3 className="text-lg font-semibold text-yellow-400 mb-4">Recent Clock History</h3>
        {clockHistory.length > 0 ? (
          <div className="space-y-2">
            {clockHistory.map((record) => (
              <div
                key={record.id}
                className="flex items-center justify-between bg-gray-800 rounded-lg p-3"
              >
                <div>
                  <div className="text-white font-medium">
                    {safeFormatDate(record.date, 'EEE, MMM d')}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {record.clockIn} - {record.clockOut || 'In Progress'}
                  </div>
                </div>
                <div className="text-right">
                  {record.totalHours && (
                    <div className="text-green-400 font-medium">{Number(record.totalHours || 0).toFixed(1)} hrs</div>
                  )}
                  <div className="text-gray-500 text-xs">{record.workstationName}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No clock history available</p>
        )}
      </Card>
    </div>
  );
}
